import React from 'react';
import { fileUrl } from '@/lib/api';

function overlayStyle(o) {
  const base = {
    position: 'absolute',
    pointerEvents: 'none',
    opacity: o.opacity ?? 0.8,
    mixBlendMode: o.blend_mode || 'normal',
    transform: `scale(${o.scale ?? 1})`,
    transformOrigin: 'center',
  };
  switch (o.position) {
    case 'top-left':     return { ...base, top: 0, left: 0, transformOrigin: 'top left' };
    case 'top-right':    return { ...base, top: 0, right: 0, transformOrigin: 'top right' };
    case 'bottom-left':  return { ...base, bottom: 0, left: 0, transformOrigin: 'bottom left' };
    case 'bottom-right': return { ...base, bottom: 0, right: 0, transformOrigin: 'bottom right' };
    case 'center':       return { ...base, top: '50%', left: '50%', transform: `translate(-50%, -50%) scale(${o.scale ?? 1})` };
    case 'full':         return { ...base, inset: 0, width: '100%', height: '100%', transform: 'none' };
    default:             return { ...base, top: 0, right: 0 };
  }
}

// Detect whether a file id should render as a <video> tag. Prefer the
// content_type hint stored on the customization payload; fall back to a
// best-effort filename suffix sniff for legacy records.
function isVideo(contentType, fileId) {
  if (typeof contentType === 'string' && contentType.startsWith('video/')) return true;
  if (typeof fileId === 'string' && /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(fileId)) return true;
  return false;
}

// Build an ordered list of <source> entries so the browser can skip codecs it
// can't decode. We don't transcode server-side, so the URL is the same — the
// `type` attribute gives Safari/Firefox a fast no-decode-needed reject path
// when their codec set doesn't match. The first entry is the actual stored
// content type (when known); we then declare common widely-supported variants
// the SAME url could be served as so the browser tries again instead of
// silently failing.
function videoSources(url, contentType) {
  const seen = new Set();
  const list = [];
  const push = (t) => { if (t && !seen.has(t)) { seen.add(t); list.push({ src: url, type: t }); } };
  if (typeof contentType === 'string' && contentType.startsWith('video/')) push(contentType);
  // Sniff suffix as a hint when content_type missing
  if (!list.length && typeof url === 'string') {
    const m = url.match(/\.(mp4|webm|mov|m4v|ogv)(?:\?|$)/i);
    if (m) push(({
      mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime',
      webm: 'video/webm', ogv: 'video/ogg',
    })[m[1].toLowerCase()]);
  }
  // Fallback chain — declare the same url under additional MIME types so
  // browsers that don't recognize an exotic content_type still try.
  push('video/mp4');
  push('video/webm');
  // Final entry: no `type` attr — last-resort raw URL probe.
  if (!seen.has('*')) list.push({ src: url, type: undefined });
  return list;
}

function VideoBg({ url, contentType, style, className, testid = 'profile-overlay' }) {
  const srcs = videoSources(url, contentType);
  return (
    <video
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      style={style}
      className={className}
      data-testid={testid}
    >
      {srcs.map((s, i) => (
        <source key={`${s.type || 'raw'}-${i}`} src={s.src} {...(s.type ? { type: s.type } : {})} />
      ))}
      {/* Plain-text fallback for assistive tech / very old browsers. */}
      Your browser can't play this video format.
    </video>
  );
}

export default function ProfileBackground({ customization, children, contained = false, wallMode = false }) {
  const c = customization || {};
  const kind = c.background_kind || 'gradient';
  let bgStyle = {};
  let bgVideoUrl = null; // when set, rendered as a fixed-cover <video>

  if (kind === 'color' && c.background_color) {
    bgStyle = { backgroundColor: c.background_color };
  } else if (kind === 'gradient') {
    const colors = (c.background_gradient && c.background_gradient.length >= 2)
      ? c.background_gradient
      : ['#0b0c10', '#1c2330'];
    bgStyle = { backgroundImage: `linear-gradient(160deg, ${colors.join(', ')})` };
  } else if ((kind === 'image' || kind === 'video') && c.background_image_file_id) {
    if (kind === 'video' || isVideo(c.background_content_type, c.background_image_file_id)) {
      bgVideoUrl = fileUrl(c.background_image_file_id);
      // dark fallback while the video buffers
      bgStyle = { backgroundColor: 'hsl(222 34% 8%)' };
    } else {
      bgStyle = {
        backgroundImage: `url(${fileUrl(c.background_image_file_id)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
  }

  // Pick the wrapper class:
  //   wallMode  → fix the section to viewport-minus-topbar so the background
  //               truly *covers* the wall section, and let inner scroll
  //               containers handle vertical overflow. We also negate the
  //               AppShell's bottom padding so the bg reaches the bottom-nav
  //               edge on mobile / the page bottom on desktop.
  //   contained → caller wants a rounded card-shaped bg (used in profile editor preview)
  //   default   → full-page background that scrolls with the page (legacy)
  const wrapperClass = wallMode
    ? 'relative flex h-[calc(100dvh-3.5rem)] flex-col overflow-hidden -mb-24 md:-mb-12'
    : (contained ? 'relative overflow-hidden rounded-[var(--radius)]' : 'relative min-h-[100dvh]');
  const innerClass = wallMode
    ? 'relative flex flex-1 flex-col overflow-hidden'
    : 'relative';

  return (
    <div className={wrapperClass} style={bgStyle} data-testid="profile-background">
      {bgVideoUrl && (
        <VideoBg
          url={bgVideoUrl}
          contentType={c.background_content_type}
          style={{ position: 'absolute', inset: 0 }}
          className="pointer-events-none h-full w-full object-cover"
          testid="profile-background-video"
        />
      )}
      {(c.overlays || []).map((o, i) => {
        if (!o || !o.file_id) return null;
        const url = fileUrl(o.file_id);
        const ovIsVideo = isVideo(o.content_type, o.file_id);
        const cls = o.position === 'full' ? 'object-cover' : 'max-w-[60%] max-h-[60%]';
        if (ovIsVideo) {
          return (
            <VideoBg
              key={`${o.file_id}-${i}`}
              url={url}
              contentType={o.content_type}
              style={overlayStyle(o)}
              className={cls}
              testid="profile-overlay"
            />
          );
        }
        return (
          <img
            key={`${o.file_id}-${i}`}
            src={url}
            alt=""
            style={overlayStyle(o)}
            className={cls}
            data-testid="profile-overlay"
          />
        );
      })}
      {/* Soft veil so foreground content stays legible */}
      <div className="pointer-events-none absolute inset-0 bg-[hsl(var(--app-bg)/0.55)]" />
      <div className={innerClass}>{children}</div>
    </div>
  );
}
